import React, { useEffect, useState } from "react";
import { doc, setDoc, addDoc, collection, serverTimestamp, runTransaction, Timestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { colors, radius, spacing, typography } from "../utils/designTokens";
import { STATUS_LABELS } from "../utils/statusColors";
import AdminModal from "./AdminModal";
import FormField from "./FormField";
import AppSelect from "./AppSelect";
import { useToast } from "./Toast";
import { FaBuilding, FaExternalLinkAlt, FaRegStickyNote, FaCheck, FaArrowLeft, FaLock, FaHandPointer, FaPaperPlane } from "react-icons/fa";
import ContactoDesarrolladora from "./ContactoDesarrolladora";
import { useIsMobile } from "../hooks/useResponsive";
import { useUser } from "../context/UserContext";
import {
  computeBloqueadoHasta, estaBloqueado, estaLibre, esTitular, puedeEditar,
  esGestion, fmtFechaLiberacion,
} from "../utils/crmAsignacion";

// Mismo pipeline que el CRM de padrones.
export const CRM_ESTADOS = [
  "sin_contactar", "contactado", "en_conversacion", "reunion", "propuesta", "ganado", "perdido", "descartado",
];
const PRIORIDADES = ["alta", "media", "baja"];

const EMPTY = {
  nombre: "", sitioWeb: "", email: "", telefono: "", ciudadZona: "", tamano: "", verificacion: "",
  estado: "sin_contactar", prioridad: "media", asignadoA: "", asignadoEmail: "",
  contactoNombre: "", contactoTel: "", contactoEmail: "",
  proximaAccion: "", proximaAccionFecha: "",
};

/**
 * Ficha CRM de una DESARROLLADORA (colección `desarrolladoras`, sin mapa). Deja
 * editar los datos de la empresa + el CRM (estado, prioridad, asignación con
 * bloqueo de 60 días, contacto, próxima acción, notas). Sirve para crear nuevas
 * (dev = null) o editar existentes. Reusa la lógica de lock de crmAsignacion.
 */
export default function DesarrolladoraFicha({ open, onClose, dev, onSaved, comerciales = [] }) {
  const { showToast } = useToast();
  const isMobile = useIsMobile();
  const { userType } = useUser();
  const email = auth.currentUser?.email || "comercial";
  const emailLc = String(email).toLowerCase();
  const isSuperAdmin = Number(userType) === 2;
  const miNombre = comerciales.find((c) => c.email === emailLc)?.name || email;

  const [form, setForm] = useState(EMPTY);
  const [notas, setNotas] = useState([]);
  const [nuevaNota, setNuevaNota] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = { ...EMPTY, ...(dev || {}) };
    // Nueva desarrolladora creada por un comercial → se reclama a sí mismo por defecto.
    if (!dev?.id && !isSuperAdmin) { base.asignadoEmail = emailLc; base.asignadoA = miNombre; }
    setForm(base);
    setNotas(Array.isArray(dev?.notas) ? dev.notas : []);
    setNuevaNota("");
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [open, dev?.id]);

  // Estado de asignación/lock (según lo guardado en `dev`).
  const nowMs = Date.now();
  const esNueva = !dev?.id;
  const bloqueado = estaBloqueado(dev, nowMs);
  const titular = esTitular(dev, emailLc);
  const libre = estaLibre(dev, nowMs);
  const fechaLib = fmtFechaLiberacion(dev);
  const editable = esNueva || puedeEditar(dev, { email: emailLc, userType }, nowMs);

  const set = (k) => (e) => editable && setForm((f) => ({ ...f, [k]: e.target.value }));

  async function guardar() {
    if (!editable) { showToast("Esta desarrolladora está asignada a otro agente.", "error"); return; }
    if (!form.nombre.trim()) { showToast("El nombre es obligatorio.", "error"); return; }
    setSaving(true);
    try {
      const nowTs = Timestamp.now();
      let notasFinal = notas;
      if (nuevaNota.trim()) {
        notasFinal = [...notas, { texto: nuevaNota.trim(), autor: email, at: new Date().toISOString() }];
      }
      const payload = { ...form, notas: notasFinal, updatedAt: serverTimestamp(), updatedBy: email };

      const prevEmail = (dev?.asignadoEmail || "");
      const newEmail = (form.asignadoEmail || "");
      const asignacionCambio = newEmail !== prevEmail;
      const historialPrev = Array.isArray(dev?.asignacionHistorial) ? dev.asignacionHistorial : [];

      if (asignacionCambio && newEmail) {
        const nombre = comerciales.find((c) => c.email === newEmail)?.name || form.asignadoA || newEmail;
        const tipo = isSuperAdmin && prevEmail && estaBloqueado(dev, nowTs.toMillis()) ? "reasignacion_superadmin" : "asignacion";
        payload.asignadoEmail = newEmail;
        payload.asignadoA = nombre;
        payload.asignadoAt = nowTs;
        payload.ultimaGestionAt = nowTs;
        payload.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
        payload.asignacionHistorial = [...historialPrev,
          { email: newEmail, name: nombre, at: new Date().toISOString(), por: email, tipo }];
      } else if (asignacionCambio && !newEmail) {
        payload.asignadoAt = null;
        payload.bloqueadoHasta = null;
        payload.liberadoAt = nowTs;
        payload.asignacionHistorial = [...historialPrev,
          { email: "", name: "", at: new Date().toISOString(), por: email, tipo: "liberacion_manual" }];
      } else if (newEmail && esGestion(dev, form, nuevaNota)) {
        payload.ultimaGestionAt = nowTs;
        payload.bloqueadoHasta = computeBloqueadoHasta(nowTs.toDate());
      }

      let id = dev?.id;
      if (!id) {
        payload.createdAt = serverTimestamp();
        payload.origen = "manual";
        const ref = await addDoc(collection(db, "desarrolladoras"), payload);
        id = ref.id;
      } else {
        const ref = doc(db, "desarrolladoras", id);
        const esReclamoComercial = !isSuperAdmin && asignacionCambio && !!newEmail;
        if (esReclamoComercial) {
          await runTransaction(db, async (tx) => {
            const snap = await tx.get(ref);
            const cur = snap.exists() ? snap.data() : {};
            if (!estaLibre(cur, Date.now())) throw new Error("OCUPADO");
            tx.set(ref, payload, { merge: true });
          });
        } else {
          await setDoc(ref, payload, { merge: true });
        }
      }

      showToast(esNueva ? "Desarrolladora creada." : "Ficha guardada.", "success");
      onSaved && onSaved(id, { ...payload, notas: notasFinal });
      onClose && onClose();
    } catch (e) {
      if (e?.message === "OCUPADO") showToast("Otro agente la tomó recién. Refrescá para ver el estado.", "error");
      else showToast(e.message || "No se pudo guardar.", "error");
    }
    setSaving(false);
  }

  const sitioUrl = form.sitioWeb
    ? (/^https?:\/\//.test(form.sitioWeb) ? form.sitioWeb : `https://${form.sitioWeb}`)
    : null;
  const notasOrden = [...notas].reverse();

  const body = (
    <>
      {!editable && (
        <div style={roBanner}>
          <FaLock size={12} /> Asignada a {dev?.asignadoA || "otro agente"} · se libera el {fechaLib || "—"}. Solo lectura hasta entonces.
        </div>
      )}

      {/* Datos de la desarrolladora */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4] }}>
        <FormField label="Nombre *" htmlFor="dev-nombre" fullWidth>
          <input id="dev-nombre" value={form.nombre} onChange={set("nombre")} disabled={!editable} placeholder="Nombre de la desarrolladora" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Sitio web" htmlFor="dev-web">
          <input id="dev-web" value={form.sitioWeb} onChange={set("sitioWeb")} disabled={!editable} placeholder="ejemplo.com.uy" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
          {sitioUrl && (
            <a href={sitioUrl} target="_blank" rel="noreferrer" style={webLink}>
              Abrir sitio <FaExternalLinkAlt size={9} />
            </a>
          )}
        </FormField>
        <FormField label="Ciudad / Zona" htmlFor="dev-zona">
          <input id="dev-zona" value={form.ciudadZona} onChange={set("ciudadZona")} disabled={!editable} placeholder="Montevideo (Pocitos)" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Email" htmlFor="dev-email">
          <input id="dev-email" type="email" value={form.email} onChange={set("email")} disabled={!editable} placeholder="contacto@empresa.uy" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Teléfono" htmlFor="dev-tel">
          <input id="dev-tel" value={form.telefono} onChange={set("telefono")} disabled={!editable} placeholder="+598 …" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Tamaño (indicio)" htmlFor="dev-tam" fullWidth>
          <input id="dev-tam" value={form.tamano} onChange={set("tamano")} disabled={!editable} placeholder="Ej. Mediana: ~13 edificios" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
      </div>

      {/* CRM */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: spacing[4], marginTop: spacing[5] }}>
        <FormField label="Estado">
          <AppSelect value={form.estado} ariaLabel="Estado" disabled={!editable}
            onChange={(v) => editable && setForm((f) => ({ ...f, estado: v }))}
            options={CRM_ESTADOS.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))} />
        </FormField>
        <FormField label="Prioridad">
          <AppSelect value={form.prioridad} ariaLabel="Prioridad" disabled={!editable}
            onChange={(v) => editable && setForm((f) => ({ ...f, prioridad: v }))}
            options={PRIORIDADES.map((e) => ({ value: e, label: STATUS_LABELS[e] || e }))} />
        </FormField>
        <FormField label="Asignado a">
          {isSuperAdmin ? (
            <>
              <AppSelect
                value={form.asignadoEmail || ""}
                ariaLabel="Asignado a"
                onChange={(em) => {
                  const c = comerciales.find((x) => x.email === em);
                  setForm((f) => ({ ...f, asignadoEmail: em, asignadoA: c ? c.name : "" }));
                }}
                options={[{ value: "", label: "— Sin asignar —" }, ...comerciales.map((c) => ({ value: c.email, label: c.name || c.email }))]}
              />
              {bloqueado && (
                <div style={lockNote}>
                  <FaLock size={10} /> Bloqueada para {dev?.asignadoA || "el agente"} hasta {fechaLib}. Reasignar queda registrado como override.
                </div>
              )}
            </>
          ) : titular ? (
            <div style={chipOk}><FaCheck size={11} /> Asignada a vos{bloqueado ? ` · se libera el ${fechaLib}` : ""}</div>
          ) : libre ? (
            form.asignadoEmail === emailLc ? (
              <div style={chipOk}><FaCheck size={11} /> Te la asignás al guardar</div>
            ) : (
              <button type="button" onClick={() => setForm((f) => ({ ...f, asignadoEmail: emailLc, asignadoA: miNombre }))} style={tomarBtn}>
                <FaHandPointer size={12} /> Tomar esta desarrolladora
              </button>
            )
          ) : (
            <div style={chipLock}><FaLock size={11} /> Asignada a {dev?.asignadoA || "otro agente"} · se libera el {fechaLib}</div>
          )}
        </FormField>

        <FormField label="Contacto — nombre" htmlFor="dev-cnom">
          <input id="dev-cnom" value={form.contactoNombre} onChange={set("contactoNombre")} disabled={!editable} placeholder="Ej. gerente comercial" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Contacto — teléfono" htmlFor="dev-ctel">
          <input id="dev-ctel" value={form.contactoTel} onChange={set("contactoTel")} disabled={!editable} placeholder="09x xxx xxx" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Contacto — email" htmlFor="dev-cmail">
          <input id="dev-cmail" type="email" value={form.contactoEmail} onChange={set("contactoEmail")} disabled={!editable} placeholder="mail@ejemplo.com" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>

        <FormField label="Próxima acción" htmlFor="dev-prox" fullWidth>
          <input id="dev-prox" value={form.proximaAccion} onChange={set("proximaAccion")} disabled={!editable} placeholder="Ej. llamar, mandar propuesta…" style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
        <FormField label="Fecha próxima acción" htmlFor="dev-proxf">
          <input id="dev-proxf" type="date" value={form.proximaAccionFecha || ""} onChange={set("proximaAccionFecha")} disabled={!editable} style={{ ...inputStyle, ...(editable ? {} : inputRO) }} />
        </FormField>
      </div>

      {/* Armar mensaje de contacto (WhatsApp / mail) + folleto */}
      {form.nombre.trim() && (
        <div style={{ marginTop: spacing[5] }}>
          <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
            <FaPaperPlane size={12} color={colors.gray400} />
            <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Armar mensaje de contacto</span>
          </div>
          <ContactoDesarrolladora dev={form} />
        </div>
      )}

      {/* Notas */}
      <div style={{ marginTop: spacing[5] }}>
        <div style={{ display: "flex", alignItems: "center", gap: spacing[2], marginBottom: spacing[2] }}>
          <FaRegStickyNote size={13} color={colors.gray400} />
          <span style={{ fontSize: typography.sizes.base, color: colors.gray700 }}>Notas de gestión</span>
        </div>
        <textarea
          value={nuevaNota}
          onChange={(e) => editable && setNuevaNota(e.target.value)}
          disabled={!editable}
          placeholder={editable ? "Agregar nota (queda con tu usuario y fecha)…" : "Tomá la desarrolladora para gestionarla."}
          rows={2}
          style={{ ...inputStyle, width: "100%", resize: "vertical", fontFamily: typography.fontFamily, ...(editable ? {} : inputRO) }}
        />
        {notasOrden.length > 0 && (
          <div style={{ marginTop: spacing[3], display: "grid", gap: spacing[2] }}>
            {notasOrden.map((n, i) => (
              <div key={i} style={notaItem}>
                <div style={{ fontSize: typography.sizes.base, color: colors.gray700, whiteSpace: "pre-wrap" }}>{n.texto}</div>
                <div style={{ fontSize: typography.sizes.xs, color: colors.gray400, marginTop: 2 }}>
                  {n.autor} · {n.at ? new Date(n.at).toLocaleString("es-UY") : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );

  if (!open) return null;

  if (isMobile) {
    return (
      <div style={fsWrap}>
        <div style={fsHeader}>
          <button onClick={onClose} style={backBtn} aria-label="Volver"><FaArrowLeft size={13} /> Volver</button>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontSize: typography.sizes.base, color: colors.gray900, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {form.nombre || (esNueva ? "Nueva desarrolladora" : "Desarrolladora")}
            </div>
          </div>
        </div>
        <div style={fsBody}>{body}</div>
        <div style={fsFooter}>
          <button onClick={onClose} style={{ ...btnGhost, flex: 1, justifyContent: "center" }}>Cancelar</button>
          <button onClick={guardar} disabled={saving || !editable} style={{ ...btnPrimary, flex: 1, justifyContent: "center" }}>
            <FaCheck size={12} /> {saving ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <AdminModal
      open={open}
      onClose={onClose}
      size="wide"
      title={esNueva ? "Nueva desarrolladora" : `Desarrolladora · ${dev?.nombre || ""}`}
      icon={<FaBuilding size={18} />}
      footer={
        <>
          <button onClick={onClose} style={btnGhost}>Cancelar</button>
          <button onClick={guardar} disabled={saving || !editable} style={btnPrimary}>
            <FaCheck size={12} /> {saving ? "Guardando…" : esNueva ? "Crear" : "Guardar ficha"}
          </button>
        </>
      }
    >
      {body}
    </AdminModal>
  );
}

const inputStyle = {
  width: "100%", padding: "11px 12px", minHeight: 44,
  border: `1px solid ${colors.gray200}`, borderRadius: radius.sm,
  fontSize: typography.sizes.base, fontFamily: typography.fontFamily,
  background: colors.white, color: colors.gray900, boxSizing: "border-box",
};
const inputRO = { background: colors.gray50, color: colors.gray600, cursor: "not-allowed" };
const roBanner = { display: "flex", alignItems: "center", gap: 6, marginBottom: spacing[4], padding: "10px 12px", borderRadius: radius.sm, background: colors.gray100, color: colors.gray700, fontSize: typography.sizes.sm };
const lockNote = { display: "flex", alignItems: "center", gap: 5, marginTop: 6, fontSize: typography.sizes.xs, color: colors.warningDark };
const chipOk = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, background: colors.successLight, color: colors.successDark, fontSize: typography.sizes.sm };
const chipLock = { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, background: colors.gray100, color: colors.gray700, fontSize: typography.sizes.sm };
const tomarBtn = { display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "10px 12px", minHeight: 42, boxSizing: "border-box", borderRadius: radius.sm, border: `1px solid ${colors.primary}`, background: colors.primaryLighter, color: colors.primary, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit" };
const webLink = { display: "inline-flex", alignItems: "center", gap: 5, marginTop: 5, fontSize: typography.sizes.sm, color: colors.primary, textDecoration: "none" };
const notaItem = { padding: spacing[3], background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm };
const fsWrap = { position: "fixed", inset: 0, zIndex: 2000, background: colors.white, display: "flex", flexDirection: "column" };
const fsHeader = { display: "flex", alignItems: "center", gap: spacing[3], padding: `${spacing[3]}px ${spacing[4]}px`, borderBottom: `1px solid ${colors.gray200}`, flexShrink: 0 };
const backBtn = { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", background: colors.primaryLighter, color: colors.primary, border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base, fontFamily: "inherit", flexShrink: 0 };
const fsBody = { flex: 1, minHeight: 0, overflowY: "auto", WebkitOverflowScrolling: "touch", padding: spacing[4] };
const fsFooter = { display: "flex", gap: spacing[2], padding: spacing[3], paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))", borderTop: `1px solid ${colors.gray200}`, background: colors.white, flexShrink: 0 };
const btnGhost = { padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.white, border: `1px solid ${colors.gray200}`, borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base, color: colors.gray700 };
const btnPrimary = { display: "inline-flex", alignItems: "center", gap: spacing[1], padding: `${spacing[2]}px ${spacing[4]}px`, background: colors.primary, color: colors.white, border: "none", borderRadius: radius.sm, cursor: "pointer", fontSize: typography.sizes.base };
